import { Component, Input, OnChanges, OnInit, SimpleChange } from '@angular/core';
import { Group } from 'src/AngularJS/Models/group.model';
import { MetricDefinition } from 'src/shared/models/pipeline-config/metric-definition.models';
import PipelineConfigurationService from '../pipeline-configuration.service';

@Component({
    selector: 'metric-summary',
    templateUrl: './metric-summary.component.html',
    styleUrls: ['./metric-summary.component.scss']
})
export class MetricSummaryComponent implements OnInit, OnChanges {

    @Input()
    definition: MetricDefinition;

    group: any = {};
    groupTypes: any = {};
    assignmentGroupsLoading: boolean = true;
    isReactivating: boolean = false;
    assignmentGroups: any[];

    private assignmentFetchPromises = new Map<number, Promise<Group[]>>();

    constructor(
        private service: PipelineConfigurationService,
    ) { }

    ngOnInit() {
        this.loadGroupTypes();
    }

    /**
     * Track changes to the definition, so that the proper rec group assignments are loaded.
     */
    ngOnChanges(changesObj: { [propName: string]: SimpleChange }) {
        if (!changesObj?.hasOwnProperty("definition"))
            return;

        this.updateAssignmentGroups(changesObj.definition.currentValue);
    }

    /**
     * Loads the group types for the Employee and Recording Hierarchy displayed text
     * Adds a null at the beginning to adjust the indexes.  Group Level 1 = index 1 (instead of index 0)
     */
    private async loadGroupTypes() {
        this.groupTypes = await this.service.getGroupTypes();
        this.groupTypes.emp.unshift(null);
        this.groupTypes.rec.unshift(null);
    }

    /**
     * Loads the rec group assignments for this metric.
     * @param definition
     */
    private async updateAssignmentGroups(definition: MetricDefinition) {
        if (!definition || !definition.AssignmentIDs?.length) {
            return;
        }

        let promise = this.assignmentFetchPromises.get(definition.MetricID);
        if (!promise) {
            // This is to avoid treating the same GroupID under different group levels. Otherwise, AngularJS avoids making a new element if it thinks they're the same.
            promise = this.service.getMetricAssignments(definition.MetricID)
                .then(groups => groups.map(g => ({ trackID: `${g.GroupLevel}_${g.GroupID}`, ...g })));
            this.assignmentFetchPromises.set(definition.MetricID, promise);
        }

        this.assignmentGroupsLoading = true;

        try {
            let groups = await promise;
            // If the user is quickly switching between definitions, assignments might load for a previous definition.
            // This check is to make sure the definition is the expected one:
            if (definition == this.definition) {
                this.assignmentGroups = groups;
                this.assignmentGroupsLoading = false;
            }
        } catch (ex) {
            console.error("Error loading metric assignments:", ex);
            this.assignmentFetchPromises.delete(definition.MetricID);
        }
    }

    /**
     * Click handler on the Reactivate button displayed for Invalid definitions.
     * Calls the endpoint to try reactivating the metric.
     */
    public async reactivateDefinition() {
        if (this.isReactivating)
            return;

        if (this.definition?.Status?.IsValid === false) {
            this.isReactivating = true;
            try {
                let response = await this.service.reactivateMetricDefinition(this.definition._id);
                if (response.status_code < 200 || response.status_code >= 300)
                    throw response;

                // reset in front-end
                this.definition.Status.IsValid = true;
                this.definition.Status.ConsecutiveFailureCount = 0;
            } catch (ex) {
                console.error("Error reactivating Metric Definition", this.definition._id, ex);
            } finally {
                this.isReactivating = false;
            }
        }
    }

    trackAssignmentGroups(index: number, item: any) {
        return item.trackID;
    }
}
